import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  ReceiptCashierLine,
  ReceiptFooterText,
  ReceiptIdentity,
  ReceiptReturnPolicy,
} from '@/components/pos/ReceiptParts';
import { PoweredBy } from '@/components/print/PoweredBy';
import { RECEIPT_LAYOUT_DEFAULTS, type ReceiptLayout } from '@/types/pos';

/**
 * POS-I227 / POS-I220: a presentational sample receipt for Settings > Receipt Customization. The
 * header, cashier line, footer and return policy come from the same components the real receipt
 * renders (ReceiptParts) and obey the same layout flags; only the item lines are samples. The
 * 58 mm / 80 mm toggle changes the preview width only (the paper width is a per-till setting).
 */
export function ReceiptPreview({
  logoUrl,
  name,
  tagline,
  ntn,
  footer,
  layout = RECEIPT_LAYOUT_DEFAULTS,
}: {
  logoUrl?: string | null;
  name?: string | null;
  tagline?: string | null;
  ntn?: string | null;
  footer?: string | null;
  layout?: ReceiptLayout;
}) {
  const { t } = useTranslation('settings');
  const { t: tr } = useTranslation('receipt');
  const [paper, setPaper] = useState<58 | 80>(80);
  const widthClass = paper === 58 ? 'w-[220px]' : 'w-[280px]';
  return (
    <div aria-label={t('previewTitle')} role="group">
      <div className="mb-2 flex items-center justify-between gap-2">
        <p className="text-sm font-medium">{t('previewTitle')}</p>
        <div role="group" aria-label={t('previewPaperWidth')} className="inline-flex overflow-hidden rounded-md border text-xs">
          {([58, 80] as const).map((w) => (
            <button
              key={w}
              type="button"
              aria-pressed={paper === w}
              onClick={() => setPaper(w)}
              className={`px-2 py-1 ${paper === w ? 'bg-primary text-primary-foreground' : 'bg-background text-foreground hover:bg-muted'}`}
            >
              {t(w === 58 ? 'previewPaper58' : 'previewPaper80')}
            </button>
          ))}
        </div>
      </div>
      <div className={`mx-auto ${widthClass} max-w-full rounded-md border bg-paper p-4 text-paper-foreground shadow-sm text-xs`}>
        <div className="text-center border-b pb-3">
          <ReceiptIdentity
            logoUrl={logoUrl}
            name={name}
            tagline={tagline}
            ntn={ntn}
            location={{ name: t('previewLocation'), address: t('previewAddress'), phone: '0300 0000000' }}
            logoPlaceholder
            layout={layout}
          />
          <ReceiptCashierLine label={tr('cashier')} name={layout.showCashier ? t('previewCashierName') : null} />
        </div>
        {layout.showCustomer && (
          <div className="border-b py-2 text-xs">
            <p>{t('previewCustomer')}: {t('previewCustomerName')}</p>
          </div>
        )}
        <div className="space-y-1 py-3 font-mono">
          <div className="flex justify-between gap-2"><span>{t('previewItemA')}</span><bdi dir="ltr">2 x 150 = 300</bdi></div>
          <div className="flex justify-between gap-2"><span>{t('previewItemB')}</span><bdi dir="ltr">1 x 200 = 200</bdi></div>
          <div className="flex justify-between gap-2 border-t pt-1"><span>{t('previewSubtotal')}</span><bdi dir="ltr">500</bdi></div>
          <div className="flex justify-between gap-2"><span>{t('previewTax')}</span><bdi dir="ltr">90</bdi></div>
          {layout.showTaxBreakdown && (
            <div className="flex justify-between gap-2 text-muted-foreground"><span>{t('previewTaxLine')}</span><bdi dir="ltr">90</bdi></div>
          )}
          <div className="flex justify-between gap-2 font-bold"><span>{t('previewTotal')}</span><bdi dir="ltr">590</bdi></div>
        </div>
        {layout.showLoyalty && (
          <div className="border-t py-2 text-xs">
            <div className="flex justify-between gap-2"><span>{t('previewLoyalty')}</span><bdi dir="ltr">+59</bdi></div>
          </div>
        )}
        <div className="text-center text-xs text-muted-foreground border-t pt-3">
          <ReceiptFooterText footer={footer} fallback={tr('thankYou')} />
          <ReceiptReturnPolicy text={layout.returnPolicy} />
          {layout.showFbrQr && (
            <div aria-hidden className="mx-auto mt-2 flex h-14 w-14 items-center justify-center rounded border border-dashed border-muted-foreground/40 text-[9px]">
              {t('previewFbrQr')}
            </div>
          )}
        </div>
        <PoweredBy />
      </div>
      <p className="mt-2 text-center text-xs text-muted-foreground">{t('previewNote')}</p>
      <p className="mt-1 text-center text-xs text-muted-foreground">{t('previewPoweredByHelp')}</p>
    </div>
  );
}
